import { useState } from 'react';
import { Zap } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useGround } from '@/hooks/useGround';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/utils';

const AMOUNTS = [21, 100, 500, 1000];

interface ZapDialogProps {
  eventId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ZapDialog({ eventId, open, onOpenChange }: ZapDialogProps) {
  const [amount, setAmount] = useState(21);
  const { settings, updateSettings, recordZap, addZapSent } = useGround();
  const { toast } = useToast();

  const send = () => {
    recordZap(eventId, amount);
    addZapSent(amount);
    toast({
      title: `Zapped ${amount.toLocaleString()} sats`,
      description: 'Demo zap — no real Lightning payment was sent.',
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <Zap className="size-5 text-primary" />
            Zap this practice
          </DialogTitle>
          <DialogDescription>
            {settings.demoMode
              ? 'Demo mode is on, so this zap is simulated locally.'
              : 'Send a real NIP-57 zap to show appreciation for this practice.'}
          </DialogDescription>
        </DialogHeader>

        {settings.demoMode ? (
          <div className="grid grid-cols-4 gap-2">
            {AMOUNTS.map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setAmount(value)}
                className={cn(
                  'rounded-lg border px-2 py-3 text-center text-sm font-medium transition-colors',
                  'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none',
                  amount === value
                    ? 'border-primary bg-primary/15 text-foreground'
                    : 'border-input text-muted-foreground hover:border-primary/40 hover:text-foreground',
                )}
              >
                <div className="tabular-nums">{value.toLocaleString()}</div>
                <div className="text-[11px] font-normal text-muted-foreground">sats</div>
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-3 rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
            <p>
              Real zaps are sent through NIP-57 using your Lightning wallet. Add an NWC connection
              string (Alby, or any NWC wallet) in <span className="text-foreground">Settings</span>{' '}
              to enable live zaps.
            </p>
            <Button variant="outline" size="sm" onClick={() => updateSettings({ demoMode: true })}>
              Enable demo zaps instead
            </Button>
          </div>
        )}

        <DialogFooter>
          {settings.demoMode && (
            <Button onClick={send} className="gap-2">
              <Zap className="size-4" />
              Send {amount.toLocaleString()} sats
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
